import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const Sunset = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.sunset-vancouver:About Sunset Paragraph">
          Sunset is a vibrant and ethnically diverse neighborhood located in the
          southeast quadrant of{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia
          </LinkText>
          . Bounded by East 41st Avenue to the north, Ontario Street to the
          west, Knight Street to the east, and the Fraser River to the south,
          Sunset encompasses approximately 6.26 square kilometers. Historically,
          the area transitioned from farmland in the 1860s to a bustling urban
          community, reflecting a rich tapestry of cultural influences. Today,
          Sunset is recognized for its multicultural ambiance, with significant
          South Asian, Chinese, Filipino, and European communities contributing
          to its unique character.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,780 - $2,180',
          bed2: '$2,294 - $4,900',
          bed3: '$3,686 - $4,086'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.sunset-vancouver:Apartment Hunting Sunset Paragraph 1">
            Finding an apartment for rent in Sunset, Vancouver, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Sunset lifestyle by exploring your new
            neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.sunset-vancouver:Apartment Hunting Sunset Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.sunset-vancouver:Apartments for rent in Sunset Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Sunset, Vancouver
              </LinkText>
              , cater to a diverse demographic, including young professionals,
              families, and students. These units range from cozy one-bedroom
              apartments to more spacious three-bedroom layouts, often featuring
              modern amenities. The neighborhood’s affordability, compared to
              other Vancouver areas, combined with its rich cultural scene and
              convenient access to public transportation, makes it an attractive
              option for renters.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.sunset-vancouver:Houses for rent in Sunset Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Sunset, Vancouver
              </LinkText>
              , are ideal for families and individuals seeking more space and
              privacy. These properties often feature multiple bedrooms, private
              gardens, and are situated on quiet, tree-lined streets. Renting a
              house in Sunset offers the benefit of a suburban lifestyle while
              remaining close to Vancouver’s urban amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.sunset-vancouver:Townhouses for rent in Sunset Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Sunset, Vancouver
              </LinkText>{' '}
              , provide a middle ground between apartments and detached houses,
              appealing to families and professionals desiring additional space
              without extensive maintenance. These multi-level homes often
              include modern designs, attached garages, and access to communal
              facilities. Renting a townhouse in Sunset allows residents to
              enjoy a community-oriented environment with convenient access to
              local amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.sunset-vancouver:Basements for rent in Sunset Paragraph">
              Basements for rent in Sunset, Vancouver, offer affordable housing
              options suitable for students, singles, or couples. These units
              typically include separate entrances, one or two bedrooms, and
              essential amenities. Renting a basement suite in Sunset provides a
              cost-effective way to reside in a desirable neighborhood with easy
              access to public transportation and local services.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.sunset-vancouver:Condos for rent Sunset Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Sunset, Vancouver
              </LinkText>
              , attract individuals and small families looking for modern living
              spaces with added amenities such as fitness centers, pools, and
              security services. These units vary in size and style, often
              boasting contemporary designs and proximity to shopping districts
              and parks. Renting a condo in Sunset combines the perks of upscale
              living with the convenience of urban accessibility.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.sunset-vancouver:Rooms for rent in Sunset Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Sunset, Vancouver
              </LinkText>{' '}
              , are ideal for students or individuals seeking budget-friendly
              accommodation. These rentals typically involve sharing common
              areas like kitchens and living rooms with other tenants. Renting a
              room in Sunset offers an affordable living solution in a
              neighborhood known for its safety, amenities, and excellent public
              transit links.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.sunset-vancouver:Short term rentals in Sunset Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Sunset, Vancouver
              </LinkText>
              , cater to tourists, business travelers, or individuals in
              transition. These accommodations range from furnished apartments
              to entire homes, providing flexibility and comfort. Opting for a
              short-term rental in Sunset allows guests to experience the
              neighborhood’s vibrant culture, access to shopping centers, and
              proximity to major city attractions.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.sunset-vancouver:Exploring Sunset Paragraph">
          Sunset, nestled in Vancouver’s southeast, is a neighborhood rich in
          cultural diversity and history. Its evolution from 19th-century
          farmland to a bustling urban area is marked by waves of immigration,
          notably from South Asia, central, and eastern Europe. This blend of
          cultures is evident along{' '}
          <LinkText href="https://www.spottedbylocals.com/vancouver/fraser-street/">
            Fraser Street
          </LinkText>{' '}
          and{' '}
          <LinkText href="https://vancouversbestplaces.com/best-places/for-shopping/vancouvers-most-interesting-streets/vancouver-main-street/">
            Main Street
          </LinkText>
          , where a variety of South Asian, Chinese, Filipino, and European
          businesses thrive.{' '}
          <LinkText href="https://vancouver.ca/home-property-development/punjabi-market.aspx">
            The Punjabi Market
          </LinkText>
          , also known as Little India, is a focal point, offering vibrant shops
          and eateries. The community’s commitment to preserving its
          multicultural heritage while embracing modernity makes Sunset a unique
          and inviting neighborhood.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.sunset-vancouver:Things To Do Sunset Paragraph">
          Sunset offers a range of activities catering to diverse interests. For
          cultural enthusiasts, the{' '}
          <LinkText href="https://vancouver.ca/home-property-development/punjabi-market.aspx">
            Punjabi Market
          </LinkText>{' '}
          provides an immersive experience into South Asian culture, with its
          array of shops, restaurants, and annual festivals. Nature lovers can
          enjoy the nearby{' '}
          <LinkText href="https://covapp.vancouver.ca/parkfinder/parkdetail.aspx?inparkid=189">
            Sunset Park
          </LinkText>
          , offering green spaces perfect for picnics and leisurely strolls.{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/sunset-community-centre.aspx">
            The Sunset Community Centre
          </LinkText>{' '}
          serves as a hub for recreational activities, including fitness
          programs, art classes, and community events. Additionally, the
          neighborhood’s proximity to the{' '}
          <LinkText href="https://vancouver.ca/green-vancouver/fraser-river.aspx">
            Fraser River
          </LinkText>
          allows for scenic walks and cycling along the riverbanks, providing a
          serene escape from urban life.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.sunset-vancouver:Restaurants Sunset Paragraph">
        <Paragraph>
          Sunset Vancouver offers a range of dining options for every taste.
          Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://www.tripadvisor.ca/restaurant_review-g154943-d762955-reviews-all_india_sweets_restaurant-vancouver_british_columbia.html">
            All India Sweets & Restaurant
          </LinkText>{' '}
          - A cornerstone of the Punjabi Market, All India Sweets & Restaurant
          has been serving authentic Indian cuisine for decades. Renowned for
          its extensive buffet and delectable sweets, this establishment offers
          a casual dining experience that transports patrons straight to the
          heart of India. Signature dishes include butter chicken, samosas, and
          an array of traditional Indian desserts. The vibrant decor and
          aromatic ambiance make it a favorite among locals and tourists alike.
          Ideal for families, groups, and anyone craving genuine Indian flavors.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://www.breka.ca/">Breka Bakery & Café</LinkText>{' '}
          - Breka Bakery & Café is a beloved spot known for its freshly baked
          goods and 24/7 service. Offering a wide selection of pastries,
          sandwiches, and beverages, it’s the perfect place for early birds and
          night owls alike. The cozy atmosphere, complemented by friendly staff,
          makes it an excellent spot for casual meetups or solo.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://originaltandoorikitchen.com/">
            Original Tandoori Kitchen
          </LinkText>{' '}
          - Original Tandoori Kitchen in Sunset, Vancouver, is a beloved spot
          for authentic Indian cuisine. Known for its flavorful tandoori dishes,
          butter chicken, and fresh naan, it offers a casual yet inviting dining
          experience. With generous portions and rich spices, it’s a go-to for
          Indian food lovers.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.sunset-vancouver:Transportation Sunset Paragraph">
          Getting around Sunset, Vancouver, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Sunset are also walkable, with key amenities and attractions
          nearby. Drivers will find a mix of street parking and parking garages
          available. No matter your preferred way of getting around, Sunset
          offers accessible and efficient transportation choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.sunset-vancouver:Where To Stay Sunset Paragraph">
          Accommodation options in Sunset, Vancouver cater to those looking for
          a more local and immersive experience. This area offers a mix of
          boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          Sunset itself, its proximity to other parts of Vancouver ensures that
          a range of traditional accommodations, from luxury to budget-friendly,
          is just a short distance away. Staying in Sunset allows visitors to
          experience its distinct character while remaining conveniently close
          to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default Sunset
